import { Injectable } from '@angular/core';
import { DriveUpdateStoreService } from '../../tabs/drive-update/services/drive-update-store.service';
import { DrivePlanStoreService } from '../../tabs/drive-plan/services/drive-plan-store.service';
import * as _ from 'lodash';
import { DriveUpdateStore } from '../../tabs/drive-update/models/drive-update';
import { EmployeeDataStoreService } from '../../tabs/employee-data/services/employee-data-store.service';
import { EngineeringPlanStoreService } from '../../tabs/engineering-plan/services/engineer-plan-store.service';
import { PendingChange, ChangeData, ChangeTab } from '../models/detect-change';
import { GlobalStoreService } from '../../global/services/global-store.service';
import { TAB_NAMES } from '../models/constants';
import { DrivePlanStore } from '../../tabs/drive-plan/models/drive-plan';
import { EmployeeDataStore } from '../../tabs/employee-data/models/employee-data';
import { EngineeringPlanStore } from '../../tabs/engineering-plan/models/engineer-plan';

@Injectable({
    providedIn: 'root'
})
export class DetectChangeService {
    constructor(
        private globalStore: GlobalStoreService,
        private employeeDataStore: EmployeeDataStoreService,
        private driveEnggPlanStore: EngineeringPlanStoreService,
        private driveUpdateStore: DriveUpdateStoreService,
        private drivePlanStore: DrivePlanStoreService) {
    }

    isPendingChanges(): boolean {

        const isAnyNewEnggPlan = this.driveEnggPlanStore.engineerPlanCached.some(this.isSomeRowChange);

        const mapDriveUpdate = (cache: DriveUpdateStore) => _
            .cloneDeep(cache.driveUpdate.Categories)
            .map(category => ({ ...category, Active: false }));
        const isPendingDriveUpdate = !_.isEqual(
            this.driveUpdateStore.driveUpdateCached.map(mapDriveUpdate),
            this.driveUpdateStore.prevDriveUpdateCached.map(mapDriveUpdate));
        const driveplanCache = _.cloneDeep(this.drivePlanStore.drivePlanCached);
        driveplanCache.map(i => i.drivePlanData.DrivePlan).forEach(a => a.forEach(b => { delete b.IsGreySelected, delete b.disabledPlan }));
        const isPendingDrivePlan = !_.isEqual(
            driveplanCache.map(i => i.drivePlanData.DrivePlan),
            this.drivePlanStore.prevDrivePlanCached.map(i => i.drivePlanData.DrivePlan));
        const isPendingEmployeeData = !_.isEqual(
            this.employeeDataStore.employeeDataCached.map(i => i.employeeData.Employee),
            this.employeeDataStore.prevEmployeeDataCached.map(i => i.employeeData.Employee));
        const enggplanCache = _.cloneDeep(this.driveEnggPlanStore.engineerPlanCached);
        enggplanCache.map(i => i.engineerPlanData.EngineeringPlan).forEach(a => a.forEach(b => { delete b.Count, delete b.disabledPlan }));
        const isPendingEnggDrivePlan = !_.isEqual(
            enggplanCache.map(i => i.engineerPlanData.EngineeringPlan),
            this.driveEnggPlanStore.prevEngineerPlanCached.map(i => i.engineerPlanData.EngineeringPlan));
        return isPendingDriveUpdate || isPendingDrivePlan || isPendingEmployeeData || isPendingEnggDrivePlan || isAnyNewEnggPlan;
    }

    getPendingChanges(): PendingChange[] {

        const mapEmployeeData = (i: EmployeeDataStore) => ({
            clientId: i.clientId,
            data: i.employeeData
        } as ChangeData);
        const mapEnggDrivePlan = (i: EngineeringPlanStore) => ({
            clientId: i.clientId,
            data: i.engineerPlanData
        } as ChangeData);
        const mapDriveUpdate = (cache: DriveUpdateStore) => ({
            clientId: cache.clientId,
            data: _
                .cloneDeep(cache.driveUpdate.Categories)
                .map(category => ({ ...category, Active: false }))
        } as ChangeData);
        const mapDrivePlan = (i: DrivePlanStore) => ({
            clientId: i.clientId,
            data: i.drivePlanData
        } as ChangeData);
        const driveplanCache = _.cloneDeep(this.drivePlanStore.drivePlanCached);
        driveplanCache.map(i => i.drivePlanData.DrivePlan).forEach(a => a.forEach(b => { delete b.IsGreySelected, delete b.disabledPlan }));
        const enggplanCache = _.cloneDeep(this.driveEnggPlanStore.engineerPlanCached);
        enggplanCache.map(i => i.engineerPlanData.EngineeringPlan).forEach(a => a.forEach(b => { delete b.Count, delete b.disabledPlan }));

        return this.createPendingChange([
            {
                name: TAB_NAMES.EMPLOYEE_DATA,
                current: this.employeeDataStore.employeeDataCached.map(mapEmployeeData),
                previous: this.employeeDataStore.prevEmployeeDataCached.map(mapEmployeeData)
            },
            {
                name: TAB_NAMES.DRIVE_ENGINEERING_PLAN,
                current: enggplanCache.map(mapEnggDrivePlan),
                previous: this.driveEnggPlanStore.prevEngineerPlanCached.map(mapEnggDrivePlan)
            },
            {
                name: TAB_NAMES.DRIVE_UPDATE,
                current: this.driveUpdateStore.driveUpdateCached.map(mapDriveUpdate),
                previous: this.driveUpdateStore.prevDriveUpdateCached.map(mapDriveUpdate)
            },
            {
                name: TAB_NAMES.DRIVE_UPDATE_PLAN,
                current: driveplanCache.map(mapDrivePlan),
                previous: this.drivePlanStore.prevDrivePlanCached.map(mapDrivePlan)
            },
        ]);
    }

    private createPendingChange(tabs: ChangeTab[]): PendingChange[] {
        const changes: PendingChange[] = [];

        tabs.forEach(tab => {
            const change: PendingChange = {
                name: tab.name,
                clients: []
            };
            tab.current.forEach(currentItem => {
                const previousItem = tab.previous.find(i => i.clientId === currentItem.clientId);
                if (!_.isEqual(currentItem.data, previousItem.data)) {
                    const client = this.globalStore.clientMaster.Clients.find(i => i.ClientId === currentItem.clientId);
                    change.clients.push({
                        id: client.ClientId,
                        name: client.ClientName
                    });
                }
            });
            changes.push(change);
        });
        const value = this.driveEnggPlanStore.engineerPlanCached.filter(this.isSomeRowChange);
        if (value.length) {
            const index = changes.findIndex(k => k.name === TAB_NAMES.DRIVE_ENGINEERING_PLAN);
            changes[index].clients = [...changes[index].clients, ...value.map(k => ({
                id: k.clientId,
                name: this.globalStore.clientMaster.Clients.find(i => i.ClientId === k.clientId)?.ClientName
            }))];
        }
        return changes;
    }

    isSomeRowChange(i: EngineeringPlanStore) {
        return [i.currentEngineer.Actions.filter(k => k.IsSelected).length,
        i.currentEngineer.EmployeeName,
        i.currentEngineer.Project,
        i.currentEngineer.Reason,
        i.currentEngineer.PlanOwner,
        i.currentEngineer.Comments.length,
        i.currentEngineer.EffectiveDate,
        i.currentEngineer.DueDate,
        i.currentEngineer.Confidence,
        i.currentEngineer.Cost,
        ].some(k => k);
    }
}
